import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import {
  CenteredCard,
  ErrorCard,
  LoadingCard,
  RetryWorkflowEventCard,
} from 'src/commons/components/Card';
import Heading from 'src/commons/components/Heading';
import { HiddenAlert } from 'src/commons/components/HiddenAlert';
import { LoadingBar } from 'src/commons/components/LoadingBar';
import { Spinner } from 'src/commons/components/Spinner';
import { SsnReentryCard } from 'src/commons/components/SsnReentryCard';
import {
  ApplicationStatus,
  ApplicationWorkflowEvent,
  GraphQLErrorCodes,
  LANDING_PAGE_OFFER_CODE_KEY,
  PageNames,
  SKIP_TMX_CLIENT_SIDE_SCRIPT_OPTIMIZELY_KEY,
  UNAUTHORIZED_MESSAGE,
} from 'src/commons/constants';
import {
  createDeviceValidationSession,
  getCreditCardDeviceValidationSessionDetails,
  processCreditCardApplicationWorkflowEvent,
} from 'src/commons/data/queries';
import { ifFeatureFlagOn } from 'src/commons/utilities/feature-flags';
import {
  logError,
  nrTrackCreditCardApplicationWorkflowEvent,
} from 'src/commons/utilities/logging';
import { prefixRoute } from 'src/commons/utilities/routes';
import { usePolling } from 'src/commons/utilities/usePolling';

// OfferWaitingRoom
// ✨🔮 Show the LoadingBar while GDS casts the fate of this application
//
// First applicationStatus transition: OFFER_IN_PROGRESS > PENDING_[X]_DEVICE_VALIDATION
// - Poll creditCardApplication to check applicationStatus once per second (60 requests max)
// - Show RetryCard to allow OFFER_RETRIED if there was an OFFER_ERROR
// - Show ErrorCard if no transition occurs after max requests
// - Redirect application-rejected users to the Decline page
//
// Start device validation session after the offer is approved to exclude rejected users
// Append TMX script to DOM upon successful response from createDeviceValidationSession
// - Poll isDeviceValidationSessionComplete once per second (15 requests max)
// - Run DEVICE_VALIDATED workflow if session completed, or after max requests
//
// After device validation, check for the next applicationStatus transition:
// Two-Step: PENDING_CONDITIONAL_DEVICE_VALIDATION > PENDING_OFFER_ACCEPT
// - Redirect offer-approved users to the Offer page
//
// One-Step: PENDING_UNCONDITIONAL_DEVICE_VALIDATION > PENDING_UNCONDITIONAL_OFFER_ACCEPT
// - Poll status while UNCONDITIONAL_VERIFICATION_IN_PROGRESS once per second (60 requests max)
// - Show RetryCard to allow VERIFICATION_RETRIED if there was an UNCONDITIONAL_VERIFICATION_ERROR
// - Show ErrorCard if no transition occurs after max requests
// - Redirect application-rejected users to the Decline page
// - Redirect verification-approved users to the Offer page
//
// Polled status                             Subsequent Logic
// ------------------------------------------------------------------------------------------------
// OFFER_IN_PROGRESS                         Continue polling getCreditCardApplication
// UNCONDITIONAL_VERIFICATION_IN_PROGRESS    Continue polling getCreditCardApplication
// PENDING_CONDITIONAL_DEVICE_VALIDATION     Run TMX script and begin polling for device validation
// PENDING_UNCONDITIONAL_DEVICE_VALIDATION   Run TMX script and begin polling for device validation
// UNCONDITIONAL_VERIFICATION_ERROR          Show RetryCard and allow VERIFICATION_RETRIED workflow
// OFFER_ERROR                               Show RetryCard and allow OFFER_RETRIED workflow
// PENDING_SSN_REENTRY                       Show SsnReentryCard and allow SSN_REENTERED workflow
// PENDING_OFFER_ACCEPT                      Route to Offer page
// PENDING_UNCONDITIONAL_OFFER_ACCEPT        Route to Offer page
// (Any other status)                        Redirect via app resume

// Constants --------------------------------------------------------------------------------------
const MAX_POLLS_APPLICATION_STATUS = 60;
const MAX_POLLS_DEVICE_VALIDATION = 15;
const PAGE_ID = 'offer-waiting-room';

// Styles -----------------------------------------------------------------------------------------
const WaitingRoomContent = styled.div`
  height: 14rem;
  width: 100%;
  max-height: 100%;
`;
WaitingRoomContent.displayName = 'WaitingRoomContent';

const WaitingRoomHeading = styled(Heading)`
  margin: 2.25rem 0 3rem;
  line-height: 2rem;
`;
WaitingRoomHeading.displayName = 'WaitingRoomHeading';

export const OfferWaitingRoom = () => {
  // Context --------------------------------------------------------------------------------------
  const {
    creditCardApplication,
    fetchAndResumeCreditCardApplication,
    fetchCreditCardApplication,
    fetchingApplication,
    pollCreditCardApplication,
  } = useContext(CreditCardApplicationContext);

  // State ----------------------------------------------------------------------------------------
  // Indicates polling result with PENDING_OFFER_ACCEPT or PENDING_UNCONDITIONAL_OFFER_ACCEPT
  const [success, setSuccess] = useState(false);
  // Shows Retry component if OFFER_ERROR or UNCONDITIONAL_VERIFICATION_ERROR
  const [showRetry, setShowRetry] = useState(false);
  // Shows SsnReentryCard if PENDING_SSN_REENTRY
  const [showReentry, setShowReentry] = useState(false);
  // Disable input while SSN reentry form submission is processing
  const [submittingSsnReentry, setSubmittingSsnReentry] = useState(false);
  // Indicates an error happened and we should show it
  const [error, setError] = useState(false);
  // Remaining attempts to poll the application for an offer status
  const [remainingStatusPolls, setRemainingStatusPolls] = useState(null);
  // Remaining attempts to poll for device validation session details
  const [remainingDevicePolls, setRemainingDevicePolls] = useState(null);

  // Hooks ----------------------------------------------------------------------------------------
  const { applicationId } = useParams();

  // Route to Offer page when two-step offer is approved or one-step verification passes
  const navigate = useNavigate();

  // Functions ------------------------------------------------------------------------------------

  // Check DOM for script with ID #tmx to determine if device profiling has already occurred
  const hasTMXScript = () => document.querySelector('#tmx') !== null;

  const startPollingDevice = () => {
    setRemainingDevicePolls(MAX_POLLS_DEVICE_VALIDATION);
  };

  const stopPollingDevice = () => {
    setRemainingDevicePolls(0);
  };

  const continuePollingDevice = (remainingPolls: number) => {
    setRemainingDevicePolls(remainingPolls - 1);
  };

  const startPollingApplication = () => {
    setRemainingStatusPolls(MAX_POLLS_APPLICATION_STATUS);
  };

  const stopPollingApplication = () => {
    setRemainingStatusPolls(0);
  };

  const runDeviceValidationWorkflowEvent = async () => {
    try {
      const response = await processCreditCardApplicationWorkflowEvent(
        applicationId,
        ApplicationWorkflowEvent.DEVICE_VALIDATED
      );
      const { data, errors } = response;
      nrTrackCreditCardApplicationWorkflowEvent(data);
      if (
        !errors &&
        data &&
        data.processCreditCardApplicationWorkflowEvent &&
        data.processCreditCardApplicationWorkflowEvent.applicationStatus &&
        data.processCreditCardApplicationWorkflowEvent.applicationStatus
      ) {
        const { applicationStatus } =
          data.processCreditCardApplicationWorkflowEvent;
        if (applicationStatus === ApplicationStatus.PENDING_OFFER_ACCEPT) {
          // After conditional device validation, the status should be PENDING_OFFER_ACCEPT
          stopPollingApplication();
          setSuccess(true);
        } else if (
          applicationStatus ===
          ApplicationStatus.UNCONDITIONAL_VERIFICATION_IN_PROGRESS
        ) {
          // After unconditional device validation, unconditional verification will begin
          // Restart polling the application until a One-Step offer decision transitions the status
          // fetch the application to update the status b/c workflow events change status
          // so we don't handle a pending device validation status for the second time
          await pollCreditCardApplication();
          startPollingApplication();
        }
        return;
      }
      if (errors && errors.length > 0 && errors[0].code) {
        // Check if GQL error indicates duplicate DEVICE_VALIDATED workflow request
        switch (errors[0].code) {
          case GraphQLErrorCodes.INVALID_WORKFLOW_EVENT_FOR_STATUS:
            if (
              /PENDING_OFFER_ACCEPT/.test(errors[0].message) ||
              /PENDING_UNCONDITIONAL_OFFER_ACCEPT/.test(errors[0].message)
            ) {
              // Go to Offer page
              stopPollingApplication();
              setSuccess(true);
            } else if (
              /UNCONDITIONAL_VERIFICATION_IN_PROGRESS/.test(errors[0].message)
            ) {
              // Restart application polling if unconditional verification is already in progress
              startPollingApplication();
            } else {
              // App resume for any other statuses following duplicate workflow request
              fetchAndResumeCreditCardApplication(PageNames.OFFER_WAITING_ROOM);
            }
            break;
          default:
            // Unknown error code, just show error
            setError(true);
        }
        return;
      }
    } catch (err: any) {
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'OfferWaitingRoom',
          function: 'runDeviceValidationWorkflowEvent',
        });
      }
    }
    // Show error when the expected status transition did not occur
    setError(true);
  };

  // Append TMX script to the DOM to begin profiling the device
  const executeTMXScript = (policyId: string, sessionId: string) => {
    try {
      // if skip TMX script execution FF is enabled, skip execution, and complete device validation
      if (ifFeatureFlagOn(SKIP_TMX_CLIENT_SIDE_SCRIPT_OPTIMIZELY_KEY)) {
        runDeviceValidationWorkflowEvent();
        return;
      }
      // Check DOM for existing script before creating a new one to avoid appending a duplicate
      if (!hasTMXScript()) {
        // Create new script element with TMX src
        const tmxScript = document.createElement('script');
        const tmxBaseSrc = 'https://img2.prosper.com/fp/tags.js';
        const tmxOrgId = `org_id=${policyId}`;
        const tmxSessionId = `session_id=${sessionId}`;
        const tmxPageId = `pageid=${PageNames.OFFER_WAITING_ROOM}`;
        tmxScript.id = 'tmx';
        tmxScript.type = 'text/javascript';
        tmxScript.src = `${tmxBaseSrc}?${tmxOrgId}&${tmxSessionId}&${tmxPageId}`;
        document.getElementsByTagName('head')[0].appendChild(tmxScript);
      }
    } catch (err: any) {
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'OfferWaitingRoom',
          function: 'executeTMXScript',
        });
      }
    }
    // Start polling session completion
    startPollingDevice();
  };

  // ThreatMetrix validation begins when status transitions to PENDING_DEVICE_VALIDATION
  const startDeviceValidation = async () => {
    const abortController = new AbortController();
    // Creates device validation script
    const pageId = PageNames.OFFER_WAITING_ROOM;
    try {
      const res = await createDeviceValidationSession(applicationId, pageId);
      if (
        res.data &&
        res.data.createCreditCardDeviceValidationSessionByApplicationId
      ) {
        const { policyId, deviceSessionId } =
          res.data.createCreditCardDeviceValidationSessionByApplicationId;
        // Create TMX script and add it to the DOM to begin profiling the device
        executeTMXScript(policyId, deviceSessionId);
      }
    } catch (err: any) {
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'OfferWaitingRoom',
          function: 'startDeviceValidation',
        });
        // Don't keep the users staring at the spinner, waiting to timeout
        setError(true);
      }
    }
    // Unsubscribe
    return () => abortController.abort();
  };

  const continuePollingApplication = (remainingPolls: number) => {
    // Decrement remaining polls
    setRemainingStatusPolls(remainingPolls - 1);
    if (remainingPolls > 1) {
      // Poll the context application again if more polls are allowed
      pollCreditCardApplication();
    } else {
      // Show ErrorCard when no more application polls remain
      setError(true);
    }
  };

  // Runs the OFFER_RETRIED or VERIFICATION_RETRIED workflow and restarts polling
  const handleRetry = async () => {
    setShowRetry(false);
    try {
      // Get the retry workflow event for the current ERROR status
      const { applicationStatus } = creditCardApplication;
      const retriedEvent =
        applicationStatus === ApplicationStatus.OFFER_ERROR
          ? ApplicationWorkflowEvent.OFFER_RETRIED
          : ApplicationWorkflowEvent.VERIFICATION_RETRIED;
      // Hit the retry workflow event
      const response = await processCreditCardApplicationWorkflowEvent(
        applicationId,
        retriedEvent
      );
      const { data, errors } = response;
      nrTrackCreditCardApplicationWorkflowEvent(data);
      // If it worked
      if (data && data.processCreditCardApplicationWorkflowEvent) {
        // Start polling again
        // Fetching the application in context will trigger the second useEffect,
        // which resets remainingStatusPolls back to the maximum starting count
        fetchCreditCardApplication();
      } else if (errors && errors.length > 0 && errors[0].code) {
        switch (errors[0].code) {
          // Unexpected status change - redirect accordingly
          case GraphQLErrorCodes.INVALID_WORKFLOW_EVENT_FOR_STATUS:
            if (
              /OFFER_IN_PROGRESS/.test(errors[0].message) ||
              /UNCONDITIONAL_VERIFICATION_IN_PROGRESS/.test(errors[0].message)
            ) {
              // Restart status polling if status already transitioned back to IN_PROGRESS
              // Fetching the application in context will trigger the second useEffect,
              // which resets remainingStatusPolls back to the maximum starting count
              fetchCreditCardApplication();
            } else {
              // If we got some other random status, just resume the application where it belongs
              fetchAndResumeCreditCardApplication(PageNames.OFFER_WAITING_ROOM);
            }
            break;
          case GraphQLErrorCodes.TERMINAL_APPLICATION:
            // If the application's terminal, resume the app where it belongs
            fetchAndResumeCreditCardApplication(PageNames.OFFER_WAITING_ROOM);
            break;
          default:
            // Unknown error code, just show error
            stopPollingApplication();
            setError(true);
        }
      } else {
        stopPollingApplication();
        setError(true);
      }
    } catch (err: any) {
      // If anything went wrong just indicate error
      stopPollingApplication();
      setError(true);
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'OfferWaitingRoom',
          function: 'handleRetry',
        });
      }
    }
  };

  // Run workflow event 'SSN_REENTERED' when user submits SSN reentry form
  const handleSsnReenteredWorkflowEvent = async (ssn: string) => {
    setSubmittingSsnReentry(true);
    const ssnReenteredEvent = ApplicationWorkflowEvent.SSN_REENTERED;
    const ssnReenteredEventAttributes = { ssn };
    try {
      // Hit the SSN_REENTERED workflow event
      const response = await processCreditCardApplicationWorkflowEvent(
        applicationId,
        ssnReenteredEvent,
        ssnReenteredEventAttributes
      );
      const { data, errors } = response;
      nrTrackCreditCardApplicationWorkflowEvent(data);
      if (data && data.processCreditCardApplicationWorkflowEvent) {
        // Start polling again to continue awaiting offer approval
        // Fetching the application in context will trigger the second useEffect,
        // which resets remainingStatusPolls back to the maximum starting count
        setShowReentry(false);
        fetchCreditCardApplication();
      } else if (errors && errors.length > 0 && errors[0].code) {
        switch (errors[0].code) {
          // Unexpected status change - redirect accordingly
          case GraphQLErrorCodes.INVALID_WORKFLOW_EVENT_FOR_STATUS:
            if (/OFFER_IN_PROGRESS/.test(errors[0].message)) {
              // Restart status polling if status is OFFER_IN_PROGRESS
              setShowReentry(false);
              fetchCreditCardApplication();
            } else {
              // App resume if any unexpected status comes through
              fetchAndResumeCreditCardApplication(PageNames.OFFER_WAITING_ROOM);
            }
            break;
          case GraphQLErrorCodes.TERMINAL_APPLICATION:
            // App resume if the application is already terminal
            fetchAndResumeCreditCardApplication(PageNames.OFFER_WAITING_ROOM);
            break;
          default:
            // Show error for any unhandled error codes and reenable input field
            setShowReentry(false);
            setError(true);
        }
      } else {
        setShowReentry(false);
        setError(true);
      }
      setSubmittingSsnReentry(false);
    } catch (err: any) {
      setShowReentry(false);
      setError(true);
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'OfferWaitingRoom',
          function: 'handleSsnReenteredWorkflowEvent',
        });
      }
    }
  };

  const onOfferApprovalPoll = () => {
    // Navigate to the offer page
    navigate(prefixRoute(`/application/${applicationId}/offer`));
  };

  // Redirect users with statuses indicating they should be elsewhere in the funnel
  useEffect(() => {
    fetchAndResumeCreditCardApplication(PageNames.OFFER_WAITING_ROOM);
    window.sessionStorage.removeItem(LANDING_PAGE_OFFER_CODE_KEY);
  }, []);

  // Begin polling application for users without an offer decision
  useEffect(() => {
    if (!fetchingApplication) {
      startPollingApplication();
    }
  }, [fetchingApplication]);

  // Run DEVICE_VALIDATED workflow when device profiling session is complete or polling expires
  useEffect(() => {
    if (remainingDevicePolls !== null && remainingDevicePolls <= 0) {
      runDeviceValidationWorkflowEvent();
    }
  }, [remainingDevicePolls]);

  // Polling --------------------------------------------------------------------------------------
  usePolling(() => {
    const pollApplication = async () => {
      // Get the latest applicationStatus from the application in context
      const { applicationStatus } = creditCardApplication;
      if (applicationStatus) {
        // Check the status and depending on a number of factors, poll more, show error, or redirect
        switch (applicationStatus) {
          case ApplicationStatus.PENDING_OFFER_ACCEPT:
          case ApplicationStatus.PENDING_UNCONDITIONAL_OFFER_ACCEPT:
            // Happy Path - go ahead and redirect
            stopPollingApplication();
            setSuccess(true);
            break;
          case ApplicationStatus.OFFER_ERROR:
          case ApplicationStatus.UNCONDITIONAL_VERIFICATION_ERROR:
            // GDS Failure - Stop polling, and show RetryWorkflowEventCard
            stopPollingApplication();
            setShowRetry(true);
            break;
          case ApplicationStatus.PENDING_CONDITIONAL_DEVICE_VALIDATION:
          case ApplicationStatus.PENDING_UNCONDITIONAL_DEVICE_VALIDATION:
            // Offer Approved - Begin device validation
            if (!hasTMXScript()) {
              // Start device validation if profiling has not yet started
              stopPollingApplication();
              startDeviceValidation();
            } else {
              continuePollingApplication(remainingStatusPolls);
            }
            break;
          case ApplicationStatus.OFFER_IN_PROGRESS:
          case ApplicationStatus.UNCONDITIONAL_VERIFICATION_IN_PROGRESS:
            // In Progress - Continue polling
            continuePollingApplication(remainingStatusPolls);
            break;
          case ApplicationStatus.PENDING_SSN_REENTRY:
            // SSN mismatch - Stop polling, and show SsnReentry
            stopPollingApplication();
            setShowReentry(true);
            break;
          default:
            // Unexpected Status - Stop polling and redirect accordingly
            stopPollingApplication();
            fetchAndResumeCreditCardApplication(PageNames.OFFER_WAITING_ROOM);
            break;
        }
      }
    };
    pollApplication();
  }, remainingStatusPolls);

  // Poll for completed TMX session profile
  usePolling(() => {
    const pollDevice = async () => {
      try {
        const { data } =
          await getCreditCardDeviceValidationSessionDetails(applicationId);
        if (data && data.getCreditCardDeviceValidationSessionDetails) {
          const { isDeviceValidationSessionComplete } =
            data.getCreditCardDeviceValidationSessionDetails;
          if (isDeviceValidationSessionComplete) {
            // Stop polling when isDeviceValidationSessionComplete comes back true
            stopPollingDevice();
          } else {
            // Decrement number of remaining polls by one if session details are not yet ready
            continuePollingDevice(remainingDevicePolls);
          }
        }
      } catch (err: any) {
        if (err.message !== UNAUTHORIZED_MESSAGE) {
          logError(err, {
            errorName: err.name || 'PageError',
            errorMessage: err.message || 'Unexpected error occurred',
            component: 'OfferWaitingRoom',
            function: 'pollDevice',
          });
        }
      }
    };
    pollDevice();
  }, remainingDevicePolls);

  if (showReentry || submittingSsnReentry) {
    return <SsnReentryCard onReenterSubmit={handleSsnReenteredWorkflowEvent} />;
  }

  if (fetchingApplication) {
    return (
      <>
        <HiddenAlert
          id={`${PAGE_ID}-loading-hidden-alert`}
          alertText="loading"
        />
        <LoadingCard />
      </>
    );
  }

  if (showRetry) {
    return <RetryWorkflowEventCard onRetry={() => handleRetry()} />;
  }

  if (error) {
    return <ErrorCard />;
  }

  return (
    <CenteredCard
      id={`${PAGE_ID}-card`}
      role="status"
      aria-labelledby={`${PAGE_ID}-heading`}
    >
      <Spinner />
      <WaitingRoomHeading id={`${PAGE_ID}-heading`}>
        Hold tight while we
        <br />
        crunch some numbers...
      </WaitingRoomHeading>
      <LoadingBar
        completionCallback={onOfferApprovalPoll}
        allowCompletion={success}
        reset={showRetry}
        completionEstimateSeconds={11}
      />
    </CenteredCard>
  );
};
